import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, Button, Heading } from "@foris/avocado-suite";
import { Classroom } from "@models/ISchema";
import { updateSlotClassrooms } from "@context/useProgrammingEditStore";
import { SlotClassroom } from "@services/types/programming-edit/programmingEditTypes";
import { useLayoutStore } from "@context/useLayoutStore";
import ClassroomPicker from "../classroom-picker";
import useClassroomSearch, { ClassroomOption } from "../../hooks/useClassroomSearch";
import styles from "./assignClassroomDrawer.module.scss";

interface AssignClassroomDrawerProps {
  slotId: string;
  currentClassrooms: SlotClassroom[];
  isOpen: boolean;
  isReadOnly?: boolean;
  onClose: () => void;
}

const AssignClassroomDrawer: React.FC<AssignClassroomDrawerProps> = ({
  slotId,
  currentClassrooms,
  isOpen,
  isReadOnly = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);
  const [selected, setSelected] = useState<Classroom[]>([]);

  const { options, loading, onSearch } = useClassroomSearch();

  useEffect(() => {
    if (isOpen) {
      setSelected(currentClassrooms as Classroom[]);
    } else {
      setSelected([]);
    }
  }, [isOpen]);

  const handleSelect = (classroom: ClassroomOption) => {
    const alreadyExists = selected.find(item => item.id === classroom.value);
    if (!alreadyExists) {
      setSelected(prev => [...prev, classroom as unknown as Classroom]);
    }
  };

  const handleDelete = (classroom: Classroom) => {
    setSelected(prev => prev.filter(item => item.id !== classroom.id));
  };

  const handleApply = () => {
    updateSlotClassrooms(
      slotId,
      selected.map(c => ({ id: c.id, name: c.name, code: c.code })),
    );
    onClose();
  };

  const handleClose = () => {
    setSelected(currentClassrooms as Classroom[]);
    onClose();
  };

  return (
    <Drawer
      className={styles.assignClassroomDrawer}
      heightGap={visibleHeaderPixels}
      title={<Heading type="h3">{t("programming-edit.assign-classroom.title")}</Heading>}
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick
      footer={
        <div className={styles.assignClassroomDrawer_footer}>
          {isReadOnly ? (
            <Button variant="ghost" onClick={handleClose}>
              {t("programming-edit.assign-classroom.close")}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={handleClose}>
                {t("programming-edit.assign-classroom.cancel")}
              </Button>
              <Button variant="primary" onClick={handleApply}>
                {t("programming-edit.assign-classroom.assign")}
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className={styles.assignClassroomDrawer_content}>
        <ClassroomPicker
          classrooms={selected}
          loading={loading}
          options={options}
          isReadOnly={isReadOnly}
          onSearch={onSearch}
          onSelect={handleSelect}
          onDelete={handleDelete}
        />
      </div>
    </Drawer>
  );
};

export default AssignClassroomDrawer;
